import AppLayout from 'features/ui/app-layout/app-layout';
import { useUserData } from 'hooks/use-user-data';
import React from 'react';
import { Navigate, useLocation } from 'react-router';
import { getPublicAuthRedirectTarget } from 'utils/auth-redirect';

interface Props {
  children?: React.ReactNode;
}

/**
 * Route guard for platform administration pages.
 *
 * `PrivateRoute` only asks whether someone is logged in, so every admin page
 * rendered for any authenticated account that typed the URL — the navigation
 * merely hid the links. Hiding is not access control, so the check lives on the
 * route itself. The API enforces the same rule independently; this exists so
 * the UI does not present pages it cannot fill.
 */
const AdminRoute: React.FC<Props> = ({ children }) => {
  const { isAuthenticated, isPlatformAdmin } = useUserData();
  const location = useLocation();

  if (!isAuthenticated()) {
    return <Navigate to={getPublicAuthRedirectTarget(location)} state={{ from: location }} replace />;
  }

  // Authenticated but not a platform admin: send them somewhere they belong
  // rather than showing an empty shell that only 403s.
  if (!isPlatformAdmin) {
    return <Navigate to="/" replace />;
  }

  return <AppLayout>{children}</AppLayout>;
};

export default AdminRoute;
